import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  TipoInforme,
  TipoInformePagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchTipoInforme = ({
  page = 1,
  nombre = '',
  page_size = 10,
}: GetTiposInformeParams) => {
  return useQuery({
    queryKey: ['tiposInforme', page, nombre],
    queryFn: async () => {
      return getTiposInforme({ page, nombre, page_size });
    },
  });
};

export const useGetTipoInforme = (id: string) => {
  return useQuery({
    queryKey: ['tiposInforme', id],
    queryFn: async () => {
      return getTipoInforme(id);
    },
  });
};

export const useCreateTipoInforme = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoInforme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposInforme'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateTipoInforme = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoInforme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposInforme'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useRemoveTipoInforme = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeTipoInforme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposInforme'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type GetTiposInformeParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};

export type CreateTipoInformeParams = Omit<TipoInforme, 'id_tipo_informe'>;
export type UpdateTipoInformeParams = {
  id: string;
  data: CreateTipoInformeParams;
};

export const getTiposInforme = async (params?: GetTiposInformeParams) => {
  const { page = 1, nombre = '', page_size = 10 } = params || {};

  return get<TipoInformePagingRes>(
    `${urlBackend}api/v1/tipos-informes/?page=${page}&nombre=${nombre}&page_size=${page_size}`,
    true
  );
};

export const getTipoInforme = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<TipoInforme>(
    `${urlBackend}api/v1/tipos-informes/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);

  return res;
};

export const createTipoInforme = async (data: CreateTipoInformeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoInforme>(
    `${urlBackend}api/v1/tipos-informes/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoInforme = async ({
  data,
  id,
}: UpdateTipoInformeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoInforme>(
    `${urlBackend}api/v1/tipos-informes/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removeTipoInforme = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<TipoInforme>(
    `${urlBackend}api/v1/tipos-informes/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
